import type { TimeSeriesPoint } from '../TimeSeriesChart.types';

/**
 * Binary search for the first index in `[fromIndex, toIndex)` whose point is past the boundary;
 * `toIndex` when none is. Points must be ascending by timestamp.
 */
function findFirstIndexPastBoundary(
  points: readonly TimeSeriesPoint[],
  isPastBoundary: (point: TimeSeriesPoint) => boolean,
  fromIndex = 0,
  toIndex = points.length
): number {
  let low = fromIndex;
  let high = toIndex;

  while (low < high) {
    const middle = Math.floor((low + high) / 2);

    if (isPastBoundary(points[middle])) {
      high = middle;
    } else {
      low = middle + 1;
    }
  }

  return low;
}

export const findIndexAtOrAfter = (
  points: readonly TimeSeriesPoint[],
  timestamp: number,
  fromIndex?: number,
  toIndex?: number
): number => findFirstIndexPastBoundary(points, (point) => point.timestamp >= timestamp, fromIndex, toIndex);

export const findIndexAfter = (
  points: readonly TimeSeriesPoint[],
  timestamp: number,
  fromIndex?: number,
  toIndex?: number
): number => findFirstIndexPastBoundary(points, (point) => point.timestamp > timestamp, fromIndex, toIndex);

export function findNearestIndexByTime(points: readonly TimeSeriesPoint[], timestamp: number): number | undefined {
  if (points.length === 0) {
    return undefined;
  }

  const afterIndex = findIndexAtOrAfter(points, timestamp);

  if (afterIndex === 0) {
    return 0;
  }

  if (afterIndex === points.length) {
    return points.length - 1;
  }

  const beforeIndex = afterIndex - 1;
  const isBeforeCloser = timestamp - points[beforeIndex].timestamp <= points[afterIndex].timestamp - timestamp;

  return isBeforeCloser ? beforeIndex : afterIndex;
}

export function findNearestIndexByValue(
  points: readonly TimeSeriesPoint[],
  fromTimestamp: number,
  toTimestamp: number,
  value: number
): number | undefined {
  const startIndex = findIndexAtOrAfter(points, fromTimestamp);
  const endIndex = findIndexAfter(points, toTimestamp, startIndex);

  if (startIndex === endIndex) {
    return undefined;
  }

  const distanceAt = (index: number) => Math.abs(points[index].value - value);

  let nearestIndex = startIndex;

  for (let index = startIndex + 1; index < endIndex; index += 1) {
    if (distanceAt(index) < distanceAt(nearestIndex)) {
      nearestIndex = index;
    }
  }

  return nearestIndex;
}
